import React, { useState } from 'react';
import {
  Input, Select, Row, Col, DatePicker,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Controller, useForm } from 'react-hook-form';
import { Dayjs } from 'dayjs';
import AlumlyButton from '../../../components/AlumlyButton';
import { DateTimeUtils } from '../../../utils/dateTime';

export interface SelectT {
  key: string;
  name: string;
}

export interface FilterOrderTransactionT {
  searchQuery: string;
  transactionType: number;
  itemType: number;
  status: number;
  startDate: Date;
  endDate: Date;
}

interface FormFilterOrderTransactionListProps {
  resetPage: Function;
  selectedFilter: FilterOrderTransactionT | undefined;
  setSelectedFilter: React.Dispatch<React.SetStateAction<any>>;
}

const FormFilterOrderTransactionList
: React.FC<FormFilterOrderTransactionListProps> = (props: FormFilterOrderTransactionListProps) => {
  const { selectedFilter, setSelectedFilter, resetPage } = props;
  const [filterActivities, setFilterActivities] = useState<SelectT[]>([]);

  const accountActivities = [
    { key: 'SIGN_IN', name: 'Sign in' },
  ];

  const orderTransactionActivities = [
    { key: 'EXPORT_DATA', name: 'Export data' },
    { key: 'CANCEL_TRANSACTION', name: 'Cancel transaction' },
    { key: 'VOID_VOUCHER', name: 'Void voucher' },
    { key: 'ADD_NOTE', name: 'Add notes' },
  ];

  const boUserActivities = [
    { key: 'CREATE', name: 'Create' },
    { key: 'UPDATE', name: 'Update' },
  ];

  const customerManagementActivities = [
    { key: 'CREATE_NON_APP_VOUCHER', name: 'Create non app voucher' },
    { key: 'DELETE_NON_APP_VOUCHER', name: 'Delete non app voucher' },
    { key: 'DEACTIVATE', name: 'Deactivate' },
    { key: 'ACTIVATE', name: 'Activate' },
    { key: 'UPLOAD_CREDIT_SUMMARY', name: 'Upload credit summary' },
    { key: 'DELETE_CREDIT_SUMMARY', name: 'Delete credit summary' },
    { key: 'UPLOAD_TRANSACTION', name: 'Upload transaction' },
    { key: 'DELETE_TRANSACTION', name: 'Delete transaction' },
  ];

  const productConfigActivities = [
    { key: 'CREATE', name: 'Create' },
    { key: 'UPDATE', name: 'Update' },
    { key: 'DELETE', name: 'Delete' },
  ];

  const courseConfigActivities = [
    { key: 'CREATE', name: 'Create' },
    { key: 'UPDATE', name: 'Update' },
    { key: 'DELETE', name: 'Delete' },
  ];

  const featuredProductConfigActivities = [
    { key: 'UPLOAD', name: 'Upload' },
    { key: 'DELETE', name: 'Delete' },
  ];

  const notificationActivities = [
    { key: 'CREATE', name: 'Create' },
    { key: 'CANCEL', name: 'Cancel' },
  ];

  const chatSettingActivities = [
    { key: 'ADD', name: 'Add' },
    { key: 'DISCONNECT', name: 'Disconnect' },
  ];

  const chatRoomActivities = [
    { key: 'ADD_NOTE', name: 'Add note' },
  ];

  const questionnaireActivities = [
    { key: 'UPDATE_PERSONAL', name: 'Update personal info' },
    { key: 'UPDATE_CONSENT_STATUS', name: 'Update consent status' },

    { key: 'UPDATE_HEALTH_QUESTIONNAIRE_STATUS', name: 'Update Health questionnaire form status' },
    { key: 'UPDATE_PRE_ARRIVAL_QUESTIONNAIRE_STATUS', name: 'Update Pre-arrival questionnaire form status' },
    { key: 'UPDATE_MEDICAL_QUESTIONNAIRE_STATUS', name: 'Update Medical questionnaire form status' },
    { key: 'UPDATE_LAB_TEST_QUESTIONNAIRE_STATUS', name: 'Update Lab test questionnaire form status' },

    { key: 'EDIT_HEALTH_QUESTIONNAIRE', name: 'Edit Health questionnaire' },
    { key: 'EDIT_PRE_ARRIVAL_QUESTIONNAIRE', name: 'Edit Pre-arrival questionnaire' },
    { key: 'EDIT_MEDICAL_QUESTIONNAIRE', name: 'Edit Medical questionnaire' },
    { key: 'EDIT_LAB_TEST_QUESTIONNAIRE', name: 'Edit Lab test questionnaire' },
  ];

  const rearrangeActivities = [
    { key: 'UPDATE', name: 'Update' },
  ];

  const { control, reset, setValue } = useForm({
    defaultValues: {
      feature: '',
      searchQuery: '',
      activity: '',
      validFromDate: null as Dayjs | null,
      validThruDate: null as Dayjs | null,
    },
  });

  const handleChangeActivities = (feature: any): void => {
    if (feature === 'ACCOUNT') {
      setFilterActivities(accountActivities);
    } else if (feature === 'BO_USER') {
      setFilterActivities(boUserActivities);
    } else if (feature === 'ORDER_TRANSACTION') {
      setFilterActivities(orderTransactionActivities);
    } else if (feature === 'CUSTOMER_MANAGEMENT') {
      setFilterActivities(customerManagementActivities);
    } else if (feature === 'PRODUCT_CONFIG') {
      setFilterActivities(productConfigActivities);
    } else if (feature === 'FEATURED_PRODUCT_CONFIG') {
      setFilterActivities(featuredProductConfigActivities);
    } else if (feature === 'COURSE_CONFIG') {
      setFilterActivities(courseConfigActivities);
    } else if (feature === 'CHAT_SETTING') {
      setFilterActivities(chatSettingActivities);
    } else if (feature === 'NOTIFICATION') {
      setFilterActivities(notificationActivities);
    } else if (feature === 'CHAT_ROOM') {
      setFilterActivities(chatRoomActivities);
    } else if (feature === 'QUESTIONNAIRE') {
      setFilterActivities(questionnaireActivities);
    } else if (feature === 'REARRANGE') {
      setFilterActivities(rearrangeActivities);
    }
    setValue('activity', '');
    setSelectedFilter({ ...selectedFilter, scope: feature, activity: undefined });
    resetPage();
  };

  const features = [
    { key: 'ACCOUNT', name: 'Account' },
    { key: 'BO_USER', name: 'Admin account' },
    { key: 'ORDER_TRANSACTION', name: 'Order transaction' },
    { key: 'CUSTOMER_MANAGEMENT', name: 'Customer management' },
    { key: 'PRODUCT_CONFIG', name: 'Product config' },
    { key: 'COURSE_CONFIG', name: 'Course config' },
    { key: 'FEATURED_PRODUCT_CONFIG', name: 'Featured product config' },
    { key: 'NOTIFICATION', name: 'Notification' },
    { key: 'CHAT_SETTING', name: 'Chat setting' },
    { key: 'CHAT_ROOM', name: 'Chat room' },
    { key: 'QUESTIONNAIRE', name: 'Questionnaire' },
    { key: 'REARRANGE', name: 'Rearrange' },
  ];

  const handleSearch = (searchQuery: string): void => {
    setSelectedFilter({ ...selectedFilter, searchQuery });
  };

  const resetSearchAndFilter = (): void => {
    reset();
    setFilterActivities([]);
    setSelectedFilter({
      searchQuery: undefined,
      feature: undefined,
      activity: undefined,
      validFromDate: undefined,
      validThruDate: undefined,
    });
  };

  return (
    <div style={{
      padding: '16px 24px',
      marginTop: 24,
      background: 'white',
      border: '1px solid #E4E4E7',
      borderRadius: 12,
    }}
    >
      <Row gutter={16} align="middle" style={{ rowGap: 16 }}>
        <Col xs={24}>
          <Controller
            name="searchQuery"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                allowClear
                size="large"
                prefix={<SearchOutlined style={{ color: '#A1A1AA' }} />}
                placeholder="Search by Email"
                value={value}
                onChange={(e) => {
                  onChange(e);
                  handleSearch(e.target.value);
                }}
              />
            )}
          />
        </Col>
        <Col xs={24} md={5}>
          <Controller
            name="validFromDate"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                style={{ width: '100%' }}
                size="large"
                format="DD/MM/YYYY"
                placeholder="Created date from"
                value={value}
                onChange={(date) => {
                  onChange(date);
                  setSelectedFilter({
                    ...selectedFilter,
                    startDate: DateTimeUtils.dateFormat(date?.toDate() || undefined),
                  });
                  resetPage();
                }}
              />
            )}
          />
        </Col>
        <Col xs={24} md={5}>
          <Controller
            name="validThruDate"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                style={{ width: '100%' }}
                size="large"
                format="DD/MM/YYYY"
                placeholder="Created date to"
                value={value}
                onChange={(date) => {
                  onChange(date);
                  setSelectedFilter({
                    ...selectedFilter,
                    endDate: DateTimeUtils.dateFormat(date?.toDate() || undefined),
                  });
                  resetPage();
                }}
              />
            )}
          />
        </Col>
        <Col xs={24} md={8}>
          <Controller
            name="feature"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                size="large"
                style={{ width: '100%' }}
                placeholder="Feature"
                allowClear
                value={value || undefined}
                onChange={(val) => {
                  onChange(val ?? '');
                  handleChangeActivities(val);
                }}
                options={features.map((feature) => ({ value: feature.key, label: feature.name }))}
              />
            )}
          />
        </Col>
        <Col xs={24} md={6}>
          <Controller
            name="activity"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                size="large"
                style={{ width: '100%' }}
                placeholder="Activity"
                allowClear
                value={value || undefined}
                notFoundContent="Empty"
                onChange={(val) => {
                  onChange(val ?? '');
                  setSelectedFilter({ ...selectedFilter, activity: val });
                  resetPage();
                }}
                options={filterActivities.map((activity) => ({ value: activity.key, label: activity.name }))}
              />
            )}
          />
        </Col>
        <Col xs={24} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <AlumlyButton label="rxvWhite" width={180} onClick={resetSearchAndFilter}>
            Reset filters
          </AlumlyButton>
        </Col>
      </Row>
    </div>
  );
};

export default FormFilterOrderTransactionList;
